import { ChangeEvent, SetStateAction } from "react";
import { Flex } from "@chakra-ui/react";
import { Dayjs } from "dayjs";
import SelectComponent from "../../../components/molecule/Select/SelectComponent";
import DatePickerComponent from "../../../components/molecule/DatePicker/DatePickerComponent";
import { Option } from "../../../types/General/Option";
import { AuthUser } from "../../../types/User/AuthUser";

export const occurrenceAlertSanTableSelectFilterData = (
  typeOptions: Option[],
  levelOptions: Option[],
  dataPeriodOptions: Option[],
  stateOptions: Option[],
  provinceOptions: Option[],
  countyOptions: Option[],
  alertSeverityOptions: Option[],
  control: any,
  setFilter: (filterName: string, value?: string) => void,
  setCreatedAtStart: (date: Dayjs | null) => void,
  setCreatedAtEnd: (date: Dayjs | null) => void,
  setSelectedType: SetStateAction<any>,
  setSelectedLevel: SetStateAction<any>,
  setSelectedDataPeriod: SetStateAction<any>,
  setSelectedState: SetStateAction<any>,
  setSelectedProvince: SetStateAction<any>,
  setSelectedCounty: SetStateAction<any>,
  setSelectedAlertSeverity: SetStateAction<any>,
  selectedProvince: Option | null,
  selectedCreatedAtStart: Dayjs | null,
  selectedCreatedAtEnd: Dayjs | null,
  alertSeverityLoaded: boolean,
  provinceLoaded: boolean,
  countyLoaded: boolean,
  typeLoaded: boolean,
  levelLoaded: boolean,
  dataPeriodLoaded: boolean,
  user: AuthUser | null | undefined,
  receivedFilters: any,
) => {
  const handleTypeChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedTypeId = e.target.value;
    const selected = typeOptions.find(
      (option) => option.id.toString() === selectedTypeId,
    );
    setSelectedType(selected || null);
    setFilter("TypeId", selectedTypeId);
  };

  const handleLevelChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedLevelId = e.target.value;
    const selected = levelOptions.find(
      (option) => option.id.toString() === selectedLevelId,
    );
    setSelectedLevel(selected || null);
    setFilter("LevelId", selectedLevelId);
  };

  const handleDataPeriodChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedDataPeriodId = e.target.value;
    const selected = dataPeriodOptions.find(
      (option) => option.id.toString() === selectedDataPeriodId,
    );
    setSelectedDataPeriod(selected || null);
    setFilter("DataPeriodId", selectedDataPeriodId);
  };

  const handleStateChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedStateId = e.target.value;
    setSelectedState(e.target.selectedOptions[0]?.text ?? "");
    setFilter("IsActive", selectedStateId);
  };

  const handleProvinceChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedProvinceId = e.target.value;
    const selected = provinceOptions.find(
      (option) => option.id.toString() === selectedProvinceId,
    );
    setSelectedProvince(selected || null);
    setFilter("ProvinceId", selectedProvinceId);
  };

  const handleCountyChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedCountyId = e.target.value;
    const selected = countyOptions.find(
      (option) => option.id.toString() === selectedCountyId,
    );
    setSelectedCounty(selected || null);
    setFilter("CountyId", selectedCountyId);
  };

  const handleAlertSeverityChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const selectedSeverityId = e.target.value;
    const selected = alertSeverityOptions.find(
      (option) => option.id.toString() === selectedSeverityId,
    );
    setSelectedAlertSeverity(selected || null);
    setFilter("AlertSeverityId", selectedSeverityId);
  };

  const handleCreatedAtStartChange = (date: Dayjs | null) => {
    setCreatedAtStart(date || null);
    setCreatedAtEnd(null);
    setFilter("CreatedAtStart", date ? date.format("YYYY-MM-DD") : undefined);
  };

  const handleCreatedAtEndChange = (date: Dayjs | null) => {
    setCreatedAtEnd(date || null);
    setFilter("CreatedAtEnd", date ? date.format("YYYY-MM-DD") : undefined);
  };

  return (
    <Flex direction="column" gap="4">
      <Flex
        justifyContent="flex-start"
        alignItems="flex-start"
        gap="4"
        width="230px"
      >
        <Flex gap="4" alignItems="flex-end">
          <DatePickerComponent
            label="Período da ocorrência:"
            fieldName="CreatedAtStart"
            selectedDate={selectedCreatedAtStart}
            onChange={handleCreatedAtStartChange}
            mode="date"
            format="DD/MM/YYYY"
            control={control}
            width="230px"
          />
          a
          <DatePickerComponent
            label={" "}
            fieldName="CreatedAtEnd"
            selectedDate={selectedCreatedAtEnd}
            onChange={handleCreatedAtEndChange}
            mode="date"
            format="DD/MM/YYYY"
            control={control}
            width="230px"
            minDate={selectedCreatedAtStart || undefined}
          />
          <SelectComponent
            fieldNameObject="TypeId"
            options={typeOptions}
            fieldName="Tipo de ocorrência"
            control={control}
            width="230px"
            onChange={handleTypeChange}
            isLoaded={typeLoaded}
          />
          <SelectComponent
            fieldNameObject="LevelId"
            options={levelOptions}
            fieldName="Nível"
            control={control}
            width="230px"
            onChange={handleLevelChange}
            isLoaded={levelLoaded}
          />
        </Flex>
      </Flex>
      <Flex
        justifyContent="flex-start"
        alignItems="flex-start"
        gap="4"
        width="230px"
      >
        <SelectComponent
          fieldNameObject="ProvinceId"
          options={provinceOptions}
          fieldName="Província"
          control={control}
          width="230px"
          onChange={handleProvinceChange}
          isLoaded={provinceLoaded}
          isDisabled={
            user?.role?.level !== undefined && user?.role.level.id >= 4
          }
          defaultValue={
            receivedFilters?.province?.id ??
            (user?.role?.level !== undefined && user?.role.level.id >= 4
              ? user.province?.id
              : "")
          }
        />
        <SelectComponent
          fieldNameObject="CountyId"
          options={countyOptions}
          fieldName="Município"
          control={control}
          width="230px"
          onChange={handleCountyChange}
          isDisabled={
            (user?.role?.level !== undefined && user?.role.level.id === 5) ||
            !selectedProvince ||
            selectedProvince.name === ""
          }
          defaultValue={
            receivedFilters?.county?.id ??
            (user?.role?.level !== undefined && user?.role.level.id === 5
              ? user.county?.id
              : "")
          }
          isLoaded={countyLoaded}
        />
        <SelectComponent
          fieldNameObject="DataPeriodId"
          options={dataPeriodOptions}
          fieldName="Período"
          control={control}
          width="230px"
          onChange={handleDataPeriodChange}
          isLoaded={dataPeriodLoaded}
        />
        <SelectComponent
          fieldNameObject="IsActive"
          options={stateOptions}
          fieldName="Estado"
          control={control}
          width="230px"
          onChange={handleStateChange}
        />
        <SelectComponent
          fieldNameObject="AlertSeverityId"
          options={alertSeverityOptions}
          fieldName="Nível do Alerta"
          control={control}
          width="230px"
          onChange={handleAlertSeverityChange}
          isLoaded={alertSeverityLoaded}
        />
      </Flex>
    </Flex>
  );
};
